import { useEffect, useRef } from 'react';

import { GlobalAccount, GUID, TaskView } from '@ndvr/customer-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { HouseholdLogoCard } from 'widgets/LogoCard';

import { useTaskViewState } from '../task-state';

interface TaskHouseholdFilterContentProps {
    tasks: TaskView[];
    householdMap: Map<string, GlobalAccount>;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    focusSearchTrigger: number;
}

export const TaskHouseholdFilter = ({
    tasks,
    householdMap,
    searchTerm,
    onSearchChange,
    focusSearchTrigger,
}: TaskHouseholdFilterContentProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get unique households from the provided task data
    const availableHouseholds = (() => {
        if (!tasks.length) return [];

        const uniqueHouseholdGuids = Array.from(
            new Set(
                tasks
                    .map((task) => task.householdGUID)
                    .filter((householdGUID): householdGUID is string => householdGUID != null),
            ),
        );

        return uniqueHouseholdGuids
            .map((guid) => {
                const household = householdMap.get(guid);
                if (!household) return null;

                return {
                    guid,
                    name: household.globalAccountNamePlain || 'Unnamed Household',
                };
            })
            .filter((household): household is NonNullable<typeof household> => household !== null)
            .sort((a, b) => a.name.localeCompare(b.name));
    })();

    // Filter households based on search term
    const filteredHouseholds = availableHouseholds.filter((household) =>
        household.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleHouseholdToggle = (householdGuid: GUID<GlobalAccount>) => {
        const currentSelected = taskViewState.selectedHouseholds;
        const isSelected = currentSelected.includes(householdGuid);

        const newSelected = isSelected
            ? currentSelected.filter((guid) => guid !== householdGuid)
            : [...currentSelected, householdGuid];

        setTaskViewState({ selectedHouseholds: newSelected });
    };

    // Focus search input when requested
    useEffect(() => {
        if (focusSearchTrigger > 0) {
            searchInputRef.current?.focus();
        }
    }, [focusSearchTrigger]);

    return (
        <div className="flex h-full flex-col space-y-3">
            <div className="p-4 pt-2">
                <Input
                    ref={searchInputRef}
                    placeholder="Search households..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                    {filteredHouseholds.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">No households found</div>
                    ) : (
                        filteredHouseholds.map((household) => {
                            const isSelected = taskViewState.selectedHouseholds.includes(household.guid);
                            return (
                                <div
                                    key={household.guid}
                                    className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                                    onClick={() => handleHouseholdToggle(household.guid)}
                                >
                                    <Checkbox checked={isSelected} />
                                    <HouseholdLogoCard name={household.name} to="" />
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};
